import { useState } from "react";
import { toast } from "sonner";
import { Alert } from "@/core/components/ui/alert/alert";
import { AutoComplete } from "@/core/components/ui/autocomplete";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { Typography } from "@/core/components/ui/typography";
import type { PayrollRosterField } from "./types";
import {
  parseBulkImport,
  type BulkImportInvalidRow,
  type BulkImportRow,
} from "./utils";

interface ImportSummary {
  fieldLabel: string;
  matched: number;
  unmatched: string[];
  invalid: BulkImportInvalidRow[];
}

interface ImportValuesDialogProps {
  open: boolean;
  fields: PayrollRosterField[];
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (
    field: PayrollRosterField,
    rows: BulkImportRow[],
  ) => Promise<{
    matched: string[];
    unmatched: string[];
    invalid: BulkImportInvalidRow[];
  }>;
}

// Bulk-imports values for a single column, keyed by ServiceTitan employee id.
// Parsing/matching live in utils.ts; this dialog just drives them and reports
// what matched. The import is persisted to the working draft immediately;
// Publish later promotes the accumulated changes to a new roster version.
export const ImportValuesDialog = ({
  open,
  fields,
  saving,
  onOpenChange,
  onApply,
}: ImportValuesDialogProps) => {
  const [fieldKey, setFieldKey] = useState(fields[0]?.key ?? "");
  const [columnSearch, setColumnSearch] = useState(fields[0]?.label ?? "");
  const [text, setText] = useState("");
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const columnItems = fields
    .filter((field) =>
      field.label.toLowerCase().includes(columnSearch.toLowerCase()),
    )
    .map((field) => ({ value: field.key, label: field.label }));

  const reset = () => {
    setText("");
    setParseErrors([]);
    setSummary(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (saving) return;
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const targetField = fields.find((field) => field.key === fieldKey);

  const apply = async () => {
    if (!targetField) return;

    const { rows, errors } = parseBulkImport(text);
    setParseErrors(errors);

    if (rows.length === 0) {
      setSummary(null);
      if (errors.length === 0) {
        toast.error("Paste at least one row to import.");
      }
      return;
    }

    let result: Awaited<ReturnType<typeof onApply>>;
    try {
      result = await onApply(targetField, rows);
    } catch {
      return;
    }
    const { matched, unmatched, invalid } = result;
    setSummary({
      fieldLabel: targetField.label,
      matched: matched.length,
      unmatched,
      invalid,
    });

    const issueDescription = [
      unmatched.length > 0
        ? `${unmatched.length} ${
            unmatched.length === 1 ? "id" : "ids"
          } did not match anyone on the roster.`
        : null,
      invalid.length > 0
        ? `${invalid.length} ${
            invalid.length === 1 ? "value was" : "values were"
          } invalid and skipped.`
        : null,
    ]
      .filter(Boolean)
      .join(" ");

    if (matched.length === 0) {
      toast.error("No values were imported", {
        description: issueDescription,
      });
    } else {
      toast.success(
        `Imported ${matched.length} ${
          matched.length === 1 ? "value" : "values"
        } into ${targetField.label}`,
        issueDescription ? { description: issueDescription } : undefined,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="p-6 text-left">
          <DialogTitle>Import values</DialogTitle>
          <Typography variant="bodyW3" textColor="secondary">
            Paste rows keyed by ServiceTitan employee id (e.g.{" "}
            <span className="font-medium">19470, 46.0</span>) — one per line,
            comma or tab separated. Matched people get the value in the chosen
            column. They are saved to the working draft so you can keep making
            changes before publishing.
          </Typography>
        </DialogHeader>

        <Stack gap="md" className="px-6">
          <Stack gap="xs">
            <Typography variant="subtitleW3" textColor="interactive">
              Column to fill
            </Typography>
            <AutoComplete
              fullWidth
              disabled={saving}
              selectedValue={fieldKey}
              onSelectedValueChange={setFieldKey}
              searchValue={columnSearch}
              onSearchValueChange={setColumnSearch}
              items={columnItems}
              placeholder="Select a column"
              emptyMessage="No columns"
            />
          </Stack>

          <Textarea
            label="Rows"
            disabled={saving}
            placeholder={"19470, 46.0\n46821, 52.5"}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />

          {parseErrors.length > 0 && (
            <Alert variant="destructive">
              <Stack gap="xs">
                {parseErrors.map((error) => (
                  <span key={error}>{error}</span>
                ))}
              </Stack>
            </Alert>
          )}

          {summary && (
            <Alert
              variant={
                summary.unmatched.length > 0 || summary.invalid.length > 0
                  ? "destructive"
                  : "info"
              }
            >
              <Stack gap="xs">
                <span>
                  Applied {summary.matched}{" "}
                  {summary.matched === 1 ? "value" : "values"} to{" "}
                  {summary.fieldLabel}.
                </span>
                {summary.unmatched.length > 0 && (
                  <span>Unmatched ids: {summary.unmatched.join(", ")}</span>
                )}
                {summary.invalid.length > 0 && (
                  <Stack gap="none">
                    {summary.invalid.map((row) => (
                      <span key={row.stId}>
                        {row.stId}: {row.reason}
                      </span>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Alert>
          )}
        </Stack>

        <DialogFooter>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => handleOpenChange(false)}
          >
            Done
          </Button>
          <Button
            loading={saving}
            disabled={saving || !targetField || !text.trim()}
            onClick={apply}
          >
            {saving ? "Saving roster..." : "Apply to roster"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
